import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalizedNav } from '../i18n/useLocalizedNav';
import { SUPPORTED_LANGS, LANG_LABELS } from '../i18n/config';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { lang, pathWithoutLang, navigate, switchLang } = useLocalizedNav();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavigation = (page, scrollToId = null) => {
    setIsMenuOpen(false);
    if (scrollToId) {
      // ホームページの特定セクションにスクロール
      if (pathWithoutLang !== '/') {
        navigate('/');
        setTimeout(() => {
          const section = document.getElementById(scrollToId);
          if (section) section.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const section = document.getElementById(scrollToId);
        if (section) section.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(page);
    }
  };

  const handleLanguageChange = (nextLang) => {
    setIsMenuOpen(false);
    switchLang(nextLang);
  };

  const styles = {
    header: {
      backgroundColor: '#ffffff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
    },
    headerContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '15px 40px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
    },
    logoImage: {
      height: '50px',
      width: 'auto',
    },
    nav: {
      display: 'flex',
      gap: '30px',
      alignItems: 'center',
    },
    langSwitcher: {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      marginLeft: '10px',
      paddingLeft: '20px',
      borderLeft: '1px solid #e5e7eb',
    },
    langButton: {
      background: 'none',
      border: 'none',
      fontSize: '13px',
      color: '#9ca3af',
      cursor: 'pointer',
      padding: '6px 8px',
      transition: 'color 0.3s',
      fontWeight: '500',
      whiteSpace: 'nowrap',
    },
    langButtonActive: {
      color: '#2563eb',
      fontWeight: '700',
      textDecoration: 'underline',
      textUnderlineOffset: '4px',
    },
    mobileLangSwitcher: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      padding: '20px 40px',
      borderTop: '1px solid #e5e7eb',
      marginTop: '10px',
    },
    mobileLangLabel: {
      padding: '20px 40px 0',
      fontSize: '13px',
      color: '#9ca3af',
      fontWeight: '500',
    },
    mobileLangButton: {
      background: 'none',
      border: '1px solid #e5e7eb',
      borderRadius: '4px',
      fontSize: '14px',
      color: '#374151',
      cursor: 'pointer',
      padding: '8px 14px',
      fontWeight: '500',
    },
    mobileLangButtonActive: {
      // border は省略記法なので、上書き側も省略記法で指定する
      // （border と borderColor を混ぜると React が再描画時に警告を出す）
      border: '1px solid #2563eb',
      color: '#2563eb',
      fontWeight: '700',
    },
    navButton: {
      background: 'none',
      border: 'none',
      fontSize: '16px',
      color: '#374151',
      cursor: 'pointer',
      padding: '8px 12px',
      transition: 'color 0.3s',
      fontWeight: '500',
    },
    hamburger: {
      display: 'none',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
      zIndex: 1002,
    },
    hamburgerLine: {
      width: '25px',
      height: '3px',
      backgroundColor: '#374151',
      margin: '5px 0',
      transition: 'all 0.3s',
    },
    mobileNav: {
      position: 'fixed',
      top: 0,
      right: isMenuOpen ? 0 : '-100%',
      width: '70%',
      maxWidth: '300px',
      height: '100vh',
      backgroundColor: '#ffffff',
      boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
      transition: 'right 0.3s ease-in-out',
      zIndex: 1001,
      display: 'flex',
      flexDirection: 'column',
      paddingTop: '80px',
    },
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: isMenuOpen ? 'block' : 'none',
      zIndex: 999,
    },
    mobileNavButton: {
      background: 'none',
      border: 'none',
      fontSize: '16px',
      color: '#374151',
      cursor: 'pointer',
      padding: '20px 40px',
      textAlign: 'left',
      transition: 'background-color 0.3s',
      fontWeight: '500',
      borderBottom: '1px solid #e5e7eb',
    },
  };

  return (
    <>
      <style>
        {`
          @media (max-width: 768px) {
            .desktop-nav {
              display: none !important;
            }
            .hamburger-button {
              display: block !important;
            }
            .header-container {
              padding: 15px 20px !important;
            }
            .logo-image {
              height: 40px !important;
            }
          }
        `}
      </style>
      
      <header style={styles.header}>
        <div style={styles.headerContainer} className="header-container">
          <div style={styles.logoContainer} onClick={() => handleNavigation('/')}>
            <img 
              src={process.env.PUBLIC_URL + '/jtclogo.png'} 
              alt="Japan Tabibito Club" 
              style={styles.logoImage}
              className="logo-image"
            />
          </div>
          
          {/* デスクトップナビ */}
          <nav style={styles.nav} className="desktop-nav">
            <button onClick={() => handleNavigation('/')} style={styles.navButton}>
              {t('header.home')}
            </button>
            <button onClick={() => handleNavigation('/news')} style={styles.navButton}>
              {t('header.news')}
            </button>
            <button onClick={() => handleNavigation('/about')} style={styles.navButton}>
              {t('header.about')}
            </button>
            <button onClick={() => handleNavigation('/', 'work')} style={styles.navButton}>
              {t('header.work')}
            </button>
            <button onClick={() => handleNavigation('/', 'contact')} style={styles.navButton}>
              {t('header.contact')}
            </button>

            {/* 言語切替 */}
            <div style={styles.langSwitcher} aria-label={t('header.languageLabel')}>
              {SUPPORTED_LANGS.map((code) => (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code)}
                  style={{
                    ...styles.langButton,
                    ...(code === lang ? styles.langButtonActive : {}),
                  }}
                  aria-current={code === lang ? 'true' : undefined}
                  lang={code}
                >
                  {LANG_LABELS[code]}
                </button>
              ))}
            </div>
          </nav>

          {/* ハンバーガーボタン */}
          <button
            style={styles.hamburger}
            className="hamburger-button"
            onClick={toggleMenu}
            aria-label={t('header.menuLabel')}
          >
            <div style={styles.hamburgerLine}></div>
            <div style={styles.hamburgerLine}></div>
            <div style={styles.hamburgerLine}></div>
          </button>
        </div>

        {/* オーバーレイ */}
        <div style={styles.overlay} onClick={toggleMenu}></div>

        {/* モバイルナビ */}
        <nav style={styles.mobileNav}>
          <button
            onClick={() => handleNavigation('/')}
            style={styles.mobileNavButton}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {t('header.home')}
          </button>
          <button
            onClick={() => handleNavigation('/news')}
            style={styles.mobileNavButton}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {t('header.news')}
          </button>
          <button
            onClick={() => handleNavigation('/about')}
            style={styles.mobileNavButton}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {t('header.about')}
          </button>
          <button
            onClick={() => handleNavigation('/', 'work')}
            style={styles.mobileNavButton}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {t('header.work')}
          </button>
          <button
            onClick={() => handleNavigation('/', 'contact')}
            style={styles.mobileNavButton}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {t('header.contact')}
          </button>

          {/* 言語切替 */}
          <div style={styles.mobileLangLabel}>{t('header.languageLabel')}</div>
          <div style={styles.mobileLangSwitcher}>
            {SUPPORTED_LANGS.map((code) => (
              <button
                key={code}
                onClick={() => handleLanguageChange(code)}
                style={{
                  ...styles.mobileLangButton,
                  ...(code === lang ? styles.mobileLangButtonActive : {}),
                }}
                aria-current={code === lang ? 'true' : undefined}
                lang={code}
              >
                {LANG_LABELS[code]}
              </button>
            ))}
          </div>
        </nav>
      </header>
    </>
  );
};

export default Header;